import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import api from "../../services/api";
import { formatPlanification } from "../../utils/planification";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function InfoItem({ label, value, fullWidth }) {
  if (!value && value !== false) return null;
  return (
    <Grid item xs={12} md={fullWidth ? 12 : 6}>
      <MDBox mb={2}>
        <MDTypography variant="caption" fontWeight="bold" sx={{
          textTransform: "uppercase", fontSize: 11,
          letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
        }}>
          {label}
        </MDTypography>
        <MDTypography variant="body2" sx={{ color: "#344767", fontWeight: 500 }}>
          {value}
        </MDTypography>
      </MDBox>
    </Grid>
  );
}

function Section({ title, icon, children }) {
  return (
    <MDBox mb={3}>
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <MDBox sx={{
          width: 28, height: 28, borderRadius: "8px",
          background: "linear-gradient(135deg, #6a1b9a, #7b1fa2)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon sx={{ color: "#fff", fontSize: 14 }}>{icon}</Icon>
        </MDBox>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767", fontSize: 15 }}>
          {title}
        </MDTypography>
      </MDBox>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={1}>
        {children}
      </Grid>
    </MDBox>
  );
}

function BadgeChip({ value, color = "#6a1b9a", bg = "#f3e5f5" }) {
  return (
    <MDBox sx={{
      display: "inline-block", padding: "4px 12px",
      borderRadius: "20px", background: bg, color,
      fontSize: 12, fontWeight: 600, mr: 0.5, mb: 0.5,
    }}>
      {value}
    </MDBox>
  );
}

function LocalisationMap({ adresse, ville, codePostal, nom, prenom, latitude, longitude }) {
  const [coords, setCoords] = useState(null);
  const [geocodeLoading, setGeocodeLoading] = useState(true);
  const [geocodeError, setGeocodeError] = useState(false);
  const adresseComplete = [adresse, codePostal, ville].filter(Boolean).join(", ");
  const { isLoaded } = useLoadScript({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });

  useEffect(() => {
    if (latitude && longitude) {
      setCoords({ lat: parseFloat(latitude), lng: parseFloat(longitude) });
      setGeocodeLoading(false);
      return;
    }
    if (!adresseComplete) { setGeocodeLoading(false); setGeocodeError(true); return; }
    fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(adresseComplete)}&key=${GOOGLE_MAPS_API_KEY}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "OK" && data.results.length > 0) {
          const location = data.results[0].geometry.location;
          setCoords({ lat: location.lat, lng: location.lng });
        } else { setGeocodeError(true); }
      })
      .catch(() => setGeocodeError(true))
      .finally(() => setGeocodeLoading(false));
  }, [adresseComplete, latitude, longitude]);

  if (geocodeLoading || !isLoaded) return (
    <MDBox display="flex" alignItems="center" justifyContent="center"
      sx={{ height: 300, background: "#fdf8ff", borderRadius: "12px", border: "1px solid #f3e5f5" }}>
      <MDTypography variant="body2" color="text">Chargement de la carte...</MDTypography>
    </MDBox>
  );

  if (geocodeError || !coords) return (
    <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1}
      sx={{ height: 200, background: "#fdf8ff", borderRadius: "12px", border: "1px solid #f3e5f5" }}>
      <Icon sx={{ fontSize: 40, color: "#e0e0e0" }}>location_off</Icon>
      <MDTypography variant="body2" color="text">Adresse introuvable sur la carte</MDTypography>
      <MDTypography variant="caption" color="text">{adresseComplete || "Aucune adresse renseignée"}</MDTypography>
    </MDBox>
  );

  return (
    <MDBox sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #f3e5f5" }}>
      <GoogleMap mapContainerStyle={{ height: "350px", width: "100%" }}
        center={coords} zoom={17}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}>
        <Marker position={coords} title={`${prenom} ${nom}`} />
      </GoogleMap>
      <MDBox px={2} py={1} sx={{ background: "#fdf8ff", borderTop: "1px solid #f3e5f5" }}>
        <MDTypography variant="caption" color="text">
          📍 {adresseComplete}
          {latitude && longitude && <span style={{ color: "#2e7d32", marginLeft: 8 }}>✓ Localisation précise</span>}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}

function EnseignantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [enseignant, setEnseignant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/enseignants/${id}`)
      .then(({ data }) => setEnseignant(data))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet enseignant ?")) return;
    try {
      await api.delete(`/enseignants/${id}`);
      navigate("/enseignants");
    } catch (error) {
      alert(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  if (loading) return (
    <DashboardLayout><DashboardNavbar />
      <MDBox pt={6}><MDTypography variant="body2" color="text">Chargement...</MDTypography></MDBox>
    </DashboardLayout>
  );

  if (!enseignant) return (
    <DashboardLayout><DashboardNavbar />
      <MDBox pt={6}><MDTypography variant="body2" color="error">Enseignant introuvable</MDTypography></MDBox>
    </DashboardLayout>
  );

  const MATIERES = {
    quoran: "Qour'an",
    arabe: "Langue arabe",
    science: "Sciences de la religion",
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={10}>

            {/* Bouton retour + actions */}
            <MDBox mb={3} display="flex" alignItems="center"
              justifyContent="space-between" flexWrap="wrap" gap={2}>
              <MDButton variant="outlined" color="secondary" size="small"
                onClick={() => navigate("/enseignants")} sx={{ borderRadius: "10px", px: 2 }}>
                <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
                <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Retour à la liste
                </MDBox>
              </MDButton>
              <MDBox display="flex" gap={1}>
                <MDButton variant="outlined" color="warning" size="small"
                  onClick={() => navigate(`/enseignants/${id}/modifier`)} sx={{ borderRadius: "10px" }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>edit</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Modifier</MDBox>
                </MDButton>
                <MDButton variant="outlined" color="error" size="small"
                  onClick={handleDelete} sx={{ borderRadius: "10px" }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>cancel</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Supprimer</MDBox>
                </MDButton>
              </MDBox>
            </MDBox>

            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

              {/* Header */}
              <MDBox mx={2} mt={-3} py={3} px={{ xs: 2, sm: 3 }} sx={{
                background: "linear-gradient(135deg, #6a1b9a 0%, #7b1fa2 100%)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(106,27,154,0.4)",
              }}>
                <MDBox display="flex" alignItems="center" gap={{ xs: 2, sm: 3 }}>
                  <MDBox sx={{
                    width: { xs: 70, sm: 110 }, height: { xs: 70, sm: 110 },
                    borderRadius: "50%",
                    border: "3px solid rgba(255,255,255,0.5)", overflow: "hidden", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "rgba(255,255,255,0.2)",
                  }}>
                    {enseignant.photo ? (
                      <img src={`${enseignant.photo}`} alt={`${enseignant.prenom} ${enseignant.nom}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <Icon style={{ fontSize: 40, color: "rgba(255,255,255,0.8)" }}>person</Icon>
                    )}
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="h4" color="white" fontWeight="bold"
                      sx={{ fontSize: { xs: "1.3rem", sm: "2rem" } }}>
                      {enseignant.prenom} {enseignant.nom}
                    </MDTypography>
                    <MDBox display="flex" gap={1} mt={0.5} flexWrap="wrap">
                      {enseignant.email && (
                        <MDTypography variant="caption" color="white" sx={{
                          opacity: 0.85, display: { xs: "none", sm: "block" },
                        }}>
                          ✉️ {enseignant.email}
                        </MDTypography>
                      )}
                      {enseignant.telephone && (
                        <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                          📞 {enseignant.telephone}
                        </MDTypography>
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>

              <MDBox pt={4} pb={4} px={{ xs: 2, sm: 4 }}>

                <Section title="Coordonnées" icon="contact_phone">
                  <InfoItem label="Adresse" value={enseignant.adresse} fullWidth />
                  <InfoItem label="Ville" value={enseignant.ville} />
                  <InfoItem label="Code postal" value={enseignant.code_postal} />
                  <InfoItem label="Téléphone" value={enseignant.telephone} />
                  <InfoItem label="Email" value={enseignant.email} />
                </Section>

                <Section title="Cours donnés" icon="menu_book">
                  <Grid item xs={12}>
                    {(enseignant.cours || []).length === 0 ? (
                      <MDBox display="flex" flexDirection="column" alignItems="center" py={3} gap={1}
                        sx={{ background: "#fdf8ff", borderRadius: "10px" }}>
                        <Icon sx={{ fontSize: 40, color: "#e0e0e0" }}>menu_book</Icon>
                        <MDTypography variant="body2" color="text">
                          Aucun cours assigné pour l&apos;instant
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <MDBox>
                        <MDBox display="flex" gap={2} mb={2} flexWrap="wrap">
                          {["quoran", "arabe", "science"].map((m) => {
                            const count = enseignant.cours.filter((c) => c.matiere === m).length;
                            if (count === 0) return null;
                            return (
                              <MDBox key={m} sx={{
                                background: m === "quoran" ? "#e8f5e9" : m === "arabe" ? "#e3f2fd" : "#fff3e0",
                                color: m === "quoran" ? "#2e7d32" : m === "arabe" ? "#1565c0" : "#e65100",
                                borderRadius: "10px", padding: "4px 14px",
                                fontSize: 12, fontWeight: 600,
                              }}>
                                {MATIERES[m]} : {count} cours
                              </MDBox>
                            );
                          })}
                        </MDBox>
                        {enseignant.cours.map((c) => (
                          <MDBox key={c.id}
                            display="flex" justifyContent="space-between" alignItems="center"
                            py={1.5} px={2} mb={1} flexWrap="wrap" gap={1}
                            sx={{ background: "#fdf8ff", borderRadius: "10px", border: "1px solid #f3e5f5" }}>
                            <MDBox>
                              <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>
                                {MATIERES[c.matiere] || c.matiere}
                              </MDTypography>
                              <MDTypography variant="caption" color="text" display="block">
                                📍 {c.lieu || "—"} · 🕐 {formatPlanification(c) || "—"}
                              </MDTypography>
                            </MDBox>
                            {c.chapitre_termine && (
                              <BadgeChip value="✓ Terminé" color="#2e7d32" bg="#e8f5e9" />
                            )}
                          </MDBox>
                        ))}
                      </MDBox>
                    )}
                  </Grid>
                </Section>

                <Section title="Localisation" icon="location_on">
                  <Grid item xs={12}>
                    <LocalisationMap adresse={enseignant.adresse} ville={enseignant.ville}
                      codePostal={enseignant.code_postal} nom={enseignant.nom} prenom={enseignant.prenom}
                      latitude={enseignant.latitude} longitude={enseignant.longitude} />
                  </Grid>
                </Section>

              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EnseignantDetail;